import React, { useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { errText } from "../biz/i18n";
import { Sheet, btnMain, btnSoft, inp, Field } from "../biz/ui";
import { MediaUploader } from "../product-form/MediaUploader";
import { useLabT } from "./i18n";
import { TextIn, Check, StatusSelect, pair } from "./fields";
import { Collapse } from "./QuestionBuilder";

const listText = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

export function ExperimentForm({ initial, defaults, onClose, onSaved }) {
  const { t, lang } = useLabT();
  const nk = lang === "ar" ? "ar" : "en";
  const [p, setP] = useState(() => ({ status: "draft", public: true, eval_category: "olfactory", notes_visibility: "visible", size_ml: defaults.size, stock: 0, price: 0, images: [], ...initial,
    notes_en: listText(initial.notes_en), notes_ar: listText(initial.notes_ar), code: initial.code || defaults.code }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      if (p.id) await api.put(`/admin/lab/experiments/${p.id}`, p);
      else await api.post("/admin/lab/experiments", p);
      toast.success(t("saved"));
      onSaved();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title={p.id ? `${t("edit")} · ${p.code}` : t("add_experiment")} onClose={onClose} testid="lab-exp-form">
      <div className="space-y-4">
        <TextIn dir={nk === "ar" ? "rtl" : "ltr"} label={t("experiment_name")} value={p[`name_${nk}`]} onChange={(v) => set(`name_${nk}`, v)} testid="lab-f-name" />
        <div className="grid grid-cols-2 gap-3">
          <TextIn dir="ltr" label={t("code")} value={p.code} onChange={(v) => set("code", v.toUpperCase())} testid="lab-f-code" />
          <TextIn type="number" label={t("size_ml")} value={p.size_ml} onChange={(v) => set("size_ml", v)} testid="lab-f-size" />
        </div>
        <TextIn area rows={2} dir={nk === "ar" ? "rtl" : "ltr"} label={t("short_desc")} value={p[`description_${nk}`]} onChange={(v) => set(`description_${nk}`, v)} testid="lab-f-desc" />
        <div className="bg-[#1C1A17] rounded-xl p-3"><MediaUploader items={p.images || []} onChange={(v) => set("images", v)} accept="image/*" box="w-16 h-20" testId="lab-f-upload" okText={t("saved")} failText="Upload failed" /></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("eval_category")}>
            <select value={p.eval_category} onChange={(e) => set("eval_category", e.target.value)} data-testid="lab-f-category" className={inp}>
              <option value="olfactory">{t("olfactory")}</option><option value="skin">{t("skin")}</option>
            </select>
          </Field>
          <StatusSelect t={t} value={p.status} onChange={(v) => set("status", v)} testid="lab-f-status" />
        </div>
        {p.eval_category === "skin" && <Check label={t("safety_confirmed")} checked={p.safety_confirmed} onChange={(v) => set("safety_confirmed", v)} testid="lab-f-safety-confirmed" />}
        <Collapse title={t("more_options")} testid="lab-f-more">
          <div className="space-y-4">
            {pair(t, p, set, "name")}
            {pair(t, p, set, "description", { area: true })}
            <Field label={t("notes_visibility")}>
              <select value={p.notes_visibility} onChange={(e) => set("notes_visibility", e.target.value)} data-testid="lab-f-visibility" className={inp}>
                {["visible", "after_first", "hidden"].map((v) => <option key={v} value={v}>{t(`vis_${v}`)}</option>)}
              </select>
            </Field>
            {pair(t, p, set, "notes")}
            {pair(t, p, set, "concept", { area: true })}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <TextIn type="number" label={t("price_single")} value={p.price} onChange={(v) => set("price", v)} testid="lab-f-price" />
              <TextIn type="number" label={t("stock_single")} value={p.stock} onChange={(v) => set("stock", v)} testid="lab-f-stock" />
              <TextIn type="date" label={t("release_date")} value={p.release_date} onChange={(v) => set("release_date", v)} testid="lab-f-date" />
            </div>
            {pair(t, p, set, "safety", { area: true, rows: 2 })}
            {pair(t, p, set, "usage", { area: true, rows: 2 })}
            <Check label={t("public")} checked={p.public} onChange={(v) => set("public", v)} testid="lab-f-public" />
          </div>
        </Collapse>
        <div className="flex gap-3 justify-end pt-2">
          <button onClick={onClose} className={btnSoft} data-testid="lab-exp-cancel">{t("cancel")}</button>
          <button onClick={save} disabled={busy} className={btnMain} data-testid="lab-exp-save">{t("save")}</button>
        </div>
      </div>
    </Sheet>
  );
}
